import { COVER_BADGE, coverButtons, coverText, coverWhen, type CoverActionKind, type CoverRequest, type CoverText, type CoverWords } from "@teamna/shared";
import { Badge, Button, type BadgeTone } from "../components/index.ts";

/**
 * One cover request (Pilates studio shape S3), as a card: what is true now for the reader, the
 * session, what happens next, and the reader's buttons. A view only, so Storybook and tests show
 * each state with no API. `cover-requests.tsx` connects it.
 *
 * The buttons come from `coverButtons` in shared, the same as the app.
 */

export type { CoverActionKind };

const TONE: Record<CoverText["tone"], BadgeTone> = { waiting: "late", done: "confirmed", closed: "quiet" };

export function CoverCard({
  cover,
  words,
  timezone,
  busy = null,
  showSession = true,
  onAction,
}: {
  cover: CoverRequest;
  words: CoverWords;
  timezone?: string;
  /** The button whose request runs now. */
  busy?: CoverActionKind | null;
  /** Off inside a session's own panel, where the session is the heading already. */
  showSession?: boolean;
  onAction?: (kind: CoverActionKind) => void;
}) {
  const text = coverText(cover, words);
  const buttons = onAction ? coverButtons(cover) : [];
  return (
    <article aria-label={text.title} className="flex flex-col gap-2 rounded-card border border-line bg-paper px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[15px] font-semibold text-ink">
          <span dir="auto">{text.title}</span>
        </h3>
        <Badge tone={TONE[text.tone]}>{COVER_BADGE[cover.status]}</Badge>
      </div>
      {showSession ? (
        <p className="text-[13.5px] font-semibold text-ink-2">
          <bdi>{coverWhen(cover, timezone)}</bdi>
        </p>
      ) : null}
      <p className="text-[13px] text-ink-2">{text.detail}</p>
      {buttons.length ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {buttons.map((button, index) => (
            <Button
              key={button.kind}
              size="sm"
              variant={button.kind === "cancel" || button.kind === "refuse" ? "ghost" : index === 0 ? "primary" : "secondary"}
              loading={busy === button.kind}
              disabled={busy !== null && busy !== button.kind}
              onClick={() => onAction?.(button.kind)}
            >
              {button.label}
            </Button>
          ))}
        </div>
      ) : null}
    </article>
  );
}
